import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { identifyUser, resetAnalytics } from "@/lib/analytics";
import { sha256Hex } from "@/lib/contactNormalize";

const PENDING_PHONE_HASH_KEY = "spotrai_pending_phone_hash";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isBanned: boolean;
  bannedReason: string | null;
  signUp: (email: string, password: string, fullName?: string, phone?: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signInWithPhone: (phone: string) => Promise<{ error: Error | null }>;
  verifyPhoneOtp: (phone: string, token: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isBanned, setIsBanned] = useState(false);
  const [bannedReason, setBannedReason] = useState<string | null>(null);

  // Check ban status whenever user changes
  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setIsBanned(false);
      setBannedReason(null);
      return;
    }
    (async () => {
      const { data } = await (supabase as any)
        .from("profiles")
        .select("is_banned, banned_reason")
        .eq("user_id", user.id)
        .maybeSingle();
      if (cancelled) return;
      setIsBanned(!!data?.is_banned);
      setBannedReason(data?.banned_reason ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        if (session?.user) {
          identifyUser(session.user.id, { email: session.user.email });
          try {
            const pending = localStorage.getItem(PENDING_PHONE_HASH_KEY);
            if (pending) {
              setTimeout(() => {
                supabase
                  .rpc("set_my_contact_hashes", { _phone_hash: pending })
                  .then(() => localStorage.removeItem(PENDING_PHONE_HASH_KEY));
              }, 0);
            }
          } catch {}
        } else if (event === "SIGNED_OUT") {
          resetAnalytics();
        }
      }
    );

    // THEN check for existing session — and validate the user still exists
    // on the server. If the account was deleted on another device (or via
    // the delete-account flow), the cached session is "valid" locally but
    // every API call returns 403 "User from sub claim in JWT does not
    // exist". Detect that and force a clean local sign-out so the user
    // can sign back in with Apple/Google instead of being stuck.
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        try {
          const { error: userError } = await supabase.auth.getUser();
          if (userError) {
            const msg = (userError.message || "").toLowerCase();
            const isMissingUser =
              msg.includes("user from sub claim") ||
              msg.includes("user not found") ||
              msg.includes("user_not_found");
            if (isMissingUser) {
              // Local-only sign out — server can't revoke a token for a
              // user that no longer exists.
              await supabase.auth.signOut({ scope: "local" }).catch(() => {});
              setSession(null);
              setUser(null);
              setLoading(false);
              return;
            }
          }
        } catch {
          // network error — keep the cached session and let it retry
        }
      }
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    })();

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, fullName?: string, phone?: string) => {
    const redirectUrl = `${window.location.origin}/`;

    // Optional phone: normalized E.164 string. We only ever persist a SHA-256
    // hash server-side (used for contact-based friend discovery).
    let phoneHash: string | null = null;
    if (phone) {
      try {
        phoneHash = await sha256Hex(phone);
      } catch {
        phoneHash = null;
      }
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
          ...(phone ? { phone } : {}),
        },
      },
    });

    if (!error && phoneHash) {
      if (data.session) {
        try {
          await supabase.rpc("set_my_contact_hashes", { _phone_hash: phoneHash });
        } catch {}
      } else {
        // No session yet (email confirmation pending) — flush after sign-in.
        try {
          localStorage.setItem(PENDING_PHONE_HASH_KEY, phoneHash);
        } catch {}
      }
    }

    return { error: error as Error | null };
  };


  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    return { error: error as Error | null };
  };

  const signInWithPhone = async (phone: string) => {
    const { error } = await supabase.auth.signInWithOtp({ phone });
    return { error: error as Error | null };
  };

  const verifyPhoneOtp = async (phone: string, token: string) => {
    const { error } = await supabase.auth.verifyOtp({ phone, token, type: 'sms' });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    // Use local scope so a stale/deleted session still clears cleanly
    // instead of failing with a 403 from the server.
    try {
      await supabase.auth.signOut({ scope: "local" });
    } catch {
      // Always clear local state even if the server call fails
    }
    setSession(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isBanned, bannedReason, signUp, signIn, signInWithPhone, verifyPhoneOtp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
